import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Button } from '@mui/material';
import { useState, type ReactElement } from 'react';
import {
  Confirm,
  CreateButton,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';

import { useHttpClient } from '@/app/providers';
import { runResourceAction } from '@/dataProvider/restDataProvider';
import { useBabyScope } from '@/resources/babyScope';
import type { ResourceAction, ResourceDescriptor } from '@/resources/descriptors';

/**
 * Actions non-CRUD : archivage et restauration.
 *
 * L'API n'expose **aucune** route `DELETE` sur ces ressources, et c'est un choix
 * du domaine, pas un oubli : supprimer un profil ou un lieu de stockage romprait
 * la tracabilite des lots qui y font reference (ADR 0010, ADR 0011). L'archivage
 * est l'operation reelle, et elle est reversible.
 *
 * Ces actions ne passent pas par le `dataProvider` : ce ne sont pas des
 * operations CRUD, et les deguiser en `update` ferait croire a React Admin qu'un
 * corps de formulaire est envoye. Elles appellent la route dediee, puis
 * rafraichissent la vue.
 */

const iconFor = (action: ResourceAction): ReactElement =>
  action.suffix === 'restore' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />;

const ActionButton = ({
  descriptor,
  action,
}: {
  descriptor: ResourceDescriptor;
  action: ResourceAction;
}): ReactElement | null => {
  const record = useRecordContext<Record<string, unknown>>();
  const { babyId } = useBabyScope();
  const http = useHttpClient();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);

  // L'action est masquee plutot que desactivee quand elle ne s'applique pas :
  // « Restaurer » sur un enregistrement actif n'a aucun sens, et un bouton grise
  // sans explication est une question sans reponse.
  if (record === undefined || action.appliesTo?.(record) === false) {
    return null;
  }

  const run = async (): Promise<void> => {
    setRunning(true);

    try {
      await runResourceAction(
        http,
        descriptor,
        babyId,
        String(record.id),
        action.suffix,
        action.method,
      );
      notify(`${action.label} : opération effectuée.`, { type: 'success' });
      refresh();
    } catch (error) {
      // Le message est deja en francais : `httpClient` a traduit le code
      // d'erreur du backend avant d'arriver ici.
      notify(error instanceof Error ? error.message : 'L’opération a échoué.', {
        type: 'error',
      });
    } finally {
      setRunning(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        startIcon={iconFor(action)}
        disabled={running}
        onClick={(event) => {
          // Sans cela, le clic remonte a la ligne du tableau et navigue vers la
          // fiche au lieu de declencher l'action.
          event.stopPropagation();

          if (action.confirmation === undefined) {
            void run();
          } else {
            setConfirmOpen(true);
          }
        }}
      >
        {action.label}
      </Button>

      {action.confirmation === undefined ? null : (
        <Confirm
          isOpen={confirmOpen}
          loading={running}
          title={action.label}
          content={action.confirmation}
          confirm={action.label}
          cancel="Annuler"
          onConfirm={() => {
            void run();
          }}
          onClose={() => {
            setConfirmOpen(false);
          }}
        />
      )}
    </>
  );
};

/**
 * @param variant `list` pour la barre d'outils de la liste (bouton de creation),
 *                `row` pour une ligne ou une fiche (actions par enregistrement)
 */
export const ResourceActionButtons = ({
  descriptor,
  variant,
}: {
  descriptor: ResourceDescriptor;
  variant: 'list' | 'row';
}): ReactElement | null => {
  if (variant === 'list') {
    // Le bouton de creation n'apparait que si une route de creation existe :
    // proposer un formulaire pour un journal en ajout seul serait un ecran fictif.
    return descriptor.capabilities.create ? <CreateButton label="Créer" /> : null;
  }

  const actions = descriptor.actions ?? [];

  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      {actions.map((action) => (
        <ActionButton key={action.name} descriptor={descriptor} action={action} />
      ))}
    </>
  );
};
